import React, { Component } from "react";
import MenuMain from "../../components/frontend/MenuMain";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Checkout extends Component {
  
  state = {
    cart: [],
    couponCode: "",
    discount: 0,
    subtotal: 0,
    grandTotal: 0,
    couponApplied: false,
    payment_option: "cod", // default payment method
    
    // customer info
    name: "",
    email: "",
    phone: "",
    address: "",
    note: "",
    
    // Dynamic variables from DB
    currency_symbol: "",
    delivery_charge: 0, // Default to 0 (Free)
    inside_delivery_area_charge: 0,
    outside_delivery_area_charge: 0,

    loading: false,
  };

  componentDidMount() {
    this.loadCart();
    this.fetchExistingData();
  }

  /* ================= FETCH DATA ================= */
  fetchExistingData = async () => {
    try {
      const res = await axios.get("/setting", {});
      const d = res?.data?.data;
      if (d) {
        this.setState({
          currency_symbol: d.currency_symbol || "",
          // Parse as float to ensure math works correctly
          inside_delivery_area_charge: parseFloat(d.inside_delivery_area_charge) || 0,
          outside_delivery_area_charge: parseFloat(d.outside_delivery_area_charge) || 0,
        });
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  /* ================= LOAD CART ================= */
  loadCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const subtotal = cart.reduce(
      (sum, item) => sum + item.price * (item.qty || 1),
      0
    );

    // Initial Grand Total = Subtotal + Current Delivery Charge (which is 0 initially)
    this.setState({
      cart,
      subtotal,
      grandTotal: subtotal,
      discount: 0,
      couponApplied: false,
      couponCode: "",
    });
  };

  /* ================= CALCULATE TOTAL HELPER ================= */
  // Central function to calculate Grand Total based on current state
  calculateTotal = () => {
    const { subtotal, discount, delivery_charge } = this.state;
    const total = subtotal - discount + delivery_charge;
    this.setState({ grandTotal: total });
  };

  /* ================= HANDLE INPUTS ================= */
  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleCouponChange = (e) => {
    this.setState({ couponCode: e.target.value });
  };

  // Handle Payment Method (Online / COD)
  handlePaymentChange = (e) => {
    this.setState({ payment_option: e.target.value });
  };

  // Handle Delivery Area Change
  handleDeliveryChange = (e) => {
    const newDeliveryCharge = Number(e.target.value);
    
    this.setState(
      { delivery_charge: newDeliveryCharge },
      () => {
        // Recalculate grand total immediately after state update
        this.calculateTotal();
      }
    );
  };

  /* ================= APPLY COUPON ================= */
  applyCoupon = async (e) => {
    e.preventDefault();

    const { couponCode, subtotal, couponApplied } = this.state;

    if (!couponCode.trim()) {
      Swal.fire("Error", "Please enter a coupon code", "error");
      return;
    }

    if (couponApplied) {
      Swal.fire("Info", "Coupon already applied", "info");
      return;
    }

    try {
      const res = await axios.get(`/apply_coupon?couponcode=${couponCode}`);

      if (res.data.status !== "success") {
        Swal.fire("Invalid Coupon", "Coupon not found", "error");
        return;
      }

      const { coupon_amount, coupon_type } = res.data;

      let discount = coupon_type === "percentage"
        ? (subtotal * coupon_amount) / 100
        : parseFloat(coupon_amount);

      // Discount cannot be more than subtotal
      discount = Math.min(discount, subtotal);

      this.setState({ discount, couponApplied: true }, () => {
        this.calculateTotal(); // Recalculate Grand Total
        Swal.fire(
          "Coupon Applied",
          `Discount: ${discount.toFixed(2)}`,
          "success"
        );
      });
    } catch {
      Swal.fire("Error", "Failed to apply coupon", "error");
    }
  };

  /* ================= REMOVE COUPON ================= */
  removeCoupon = () => {
    this.setState({
      discount: 0,
      couponCode: "",
      couponApplied: false,
    }, () => {
      this.calculateTotal(); // Recalculate Grand Total
    });
  };

  /* ================= PLACE ORDER ================= */
  placeOrder = async () => {
    const { 
      cart, delivery_charge, discount, payment_option, grandTotal, 
      name, email, phone, address, note, subtotal 
    } = this.state;

    if (!cart.length) {
      Swal.fire("Error", "Your cart is empty", "error");
      return;
    }

    if (!name || !email || !phone || !address) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    const payload = {
      customer: {
        name,
        email,
        phone,
        address,
        note,
      },
      items: cart.map(item => ({
        id: item.id,
        qty: item.qty,
        price: item.price,
      })),
      subtotal: subtotal,
      discount: discount,
      grand_total: grandTotal, // Includes delivery charge
      payment_type: payment_option,
      order_from: "Website",
      order_status: "pending",
      payment_status: "unpaid",
      delivery_charge: delivery_charge, // Send the selected delivery charge
      currency_symbol: this.state.currency_symbol,
    };

    try {
      this.setState({ loading: true });

      const res = await axios.post("/place_order", payload, { headers: { "Content-Type": "application/json" } });

      if (res.data.status === "success") {
        Swal.fire("Success", "Order placed successfully!", "success");
        localStorage.removeItem("cart");
        this.loadCart();
        this.setState({ 
          name: "", email: "", phone: "", address: "", note: "",
          delivery_charge: 0, // Reset delivery charge
          inside_delivery_area_charge: 0, // Reset logic variables if needed, or keep them
        });
        // Optionally redirect user here
      }
    } catch (error) {
      console.error(error.response?.data || error.message);
      Swal.fire("Error", "Server error while placing order", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      cart, subtotal, discount, grandTotal,
      couponCode, couponApplied, loading,
      name, email, phone, address, note, 
      currency_symbol, 
      inside_delivery_area_charge, 
      outside_delivery_area_charge,
      delivery_charge,
      payment_option
    } = this.state;

    return (
      <>
        <MenuMain />

        <main className="main">
          <div className="page-header breadcrumb-wrap">
            <div className="container">
              <div className="breadcrumb">
                <a href="/" rel="nofollow">
                  <i className="fi-rs-home mr-5" /> Home
                </a>
                <span /> Shop <span /> Checkout
              </div>
            </div>
          </div>

          <div className="container mb-80 mt-50">
            <h1 className="heading-2 mb-40">Checkout</h1>

            <div className="row">
              {/* ================= LEFT ================= */}
              <div className="col-lg-7">
                <form className="apply-coupon mb-40" onSubmit={this.applyCoupon}>
                  <input
                    type="text"
                    placeholder="Enter Coupon Code..."
                    value={couponCode}
                    onChange={this.handleCouponChange}
                  />
                  <button className="btn btn-md">Apply Coupon</button>

                  {couponApplied && (
                    <button
                      type="button"
                      className="btn btn-md ml-10"
                      onClick={this.removeCoupon}
                    >
                      Remove
                    </button>
                  )}
                </form>

                <h4 className="mb-30">Details</h4>

                <input
                  name="name"
                  placeholder="Full Name *"
                  value={name}
                  onChange={this.handleInputChange}
                />
                <input
                  name="address"
                  placeholder="Shipping Address *"
                  value={address}
                  onChange={this.handleInputChange}
                />
                <input
                  name="email"
                  placeholder="Email *"
                  value={email}
                  onChange={this.handleInputChange}
                />
                <input
                  name="phone"
                  placeholder="Phone *"
                  value={phone}
                  onChange={this.handleInputChange}
                />
                <textarea
                  name="note"
                  rows="5"
                  placeholder="Note"
                  value={note}
                  onChange={this.handleInputChange}
                />
              </div>

              {/* ================= RIGHT ================= */}
              <div className="col-lg-5">
                <div className="border p-40 cart-totals">
                  <h4>Your Order</h4>

                  <table className="table no-border">
                    <tbody>
                      {cart.length === 0 ? (
                        <tr><td>Your cart is empty</td></tr>
                      ) : (
                        cart.map((item, i) => (
                          <tr key={i}>
                            <td><img src={item.image} alt="" width="50" /></td>
                            <td>{item.name}</td>
                            <td>{item.qty} x</td>
                            <td>{currency_symbol}{(item.price * item.qty).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* DELIVERY AREA SELECTION */}
                  <ul className="list-none mt-20">
                    <h6>Select Delivery Area:</h6>
                    <br />

                    <li className="d-flex justify-content-between">
                      <span>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="delivery_area"
                          id="inside_dhaka"
                          value={inside_delivery_area_charge}
                          checked={delivery_charge === inside_delivery_area_charge && inside_delivery_area_charge !== 0}
                          onChange={this.handleDeliveryChange}
                        /> 
                        <label htmlFor="inside_dhaka" className="ml-5">Inside Dhaka</label>
                      </span>
                      <span>{currency_symbol}{inside_delivery_area_charge}</span>
                    </li>

                    <li className="d-flex justify-content-between">
                      <span>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="delivery_area"
                          id="outside_dhaka"
                          value={outside_delivery_area_charge}
                          checked={delivery_charge === outside_delivery_area_charge && outside_delivery_area_charge !== 0}
                          onChange={this.handleDeliveryChange}
                        /> 
                         <label htmlFor="outside_dhaka" className="ml-5">Outside Dhaka</label>
                      </span>
                      <span>{currency_symbol}{outside_delivery_area_charge}</span>
                    </li>

                    <li className="d-flex justify-content-between">
                      <span>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="delivery_area"
                          id="free_delivery"
                          value="0"
                          checked={delivery_charge === 0}
                          onChange={this.handleDeliveryChange}
                        /> 
                         <label htmlFor="free_delivery" className="ml-5">Free Delivery</label>
                      </span>
                      <span>Free</span>
                    </li>
                  </ul>


                  <hr></hr>
                  {/* CALCULATION TABLE */}
                  <ul className="list-none mt-20">
                    <li className="d-flex justify-content-between font-weight-bold">
                      <span>Subtotal</span>
                      <span>{currency_symbol}{subtotal.toFixed(2)}</span>
                    </li>

                    {discount > 0 && (
                      <li className="d-flex justify-content-between">
                        <span>Coupon Discount</span>
                        <span>- {currency_symbol}{discount.toFixed(2)}</span>
                      </li>
                    )}

                    <li className="d-flex justify-content-between">
                      <span>Delivery Charge</span>
                      <span>
                         {delivery_charge === 0 ? "Free" : `${currency_symbol}${delivery_charge.toFixed(2)}`}
                      </span>
                    </li>
                    <hr></hr>
                    <li className="d-flex justify-content-between font-weight-bold">
                      <span>Grand Total</span>
                      <span>{currency_symbol}{grandTotal.toFixed(2)}</span>
                    </li>
                  </ul>


                  <div className="payment ml-0 mt-30">
                    <h6>Payment Option:</h6>
                    <br></br>
                    <div className="payment_option">

                      <div className="custome-radio">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="payment_option"
                          id="payment_online"
                          value="online"
                          checked={payment_option === "online"}
                          onChange={this.handlePaymentChange}
                        />
                        <label className="form-check-label" htmlFor="payment_online">
                          Online Gateway
                        </label>
                      </div>

                      <div className="custome-radio">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="payment_option"
                          id="payment_cod"
                          value="cod"
                          checked={payment_option === "cod"}
                          onChange={this.handlePaymentChange}
                        />
                        <label className="form-check-label" htmlFor="payment_cod">
                          Cash on delivery
                        </label>
                      </div>

                    </div>
                  </div>

                  <button
                    className="btn btn-fill-out btn-block mt-30"
                    onClick={this.placeOrder}
                    disabled={loading}
                  >
                    {loading ? "Placing Order..." : "Place an Order"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }
}

export default Checkout;